import React, { useCallback, useEffect } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import ApplicationDetailsInterface from '@api/requests/applications/interfaces/ApplicationDetailsInterface.interface';

import { getApplicationDetails } from '@app/redux/applications/applications.selectors';
import { useAppSelector } from '@app/redux/hooks';

import Loader from '@components/loader';

import AccountDetailsSkeleton from '@pages/Applications/components/AccountDetailsSkeleton';

type WithApplicationValidationsProps = {
	showLoader?: boolean;
	applicationStatusRefresh: ({
		applicationId,
		formId,
		formVersion,
	}: {
		applicationId: number;
		formId: number;
		formVersion?: number | null | undefined;
	}) => Promise<void>;
};

function withApplicationValidations<TProps extends WithApplicationValidationsProps>(
	WrappedComponent: React.JSXElementConstructor<TProps>,
) {
	return function RenderProps(props: TProps) {
		const applicationDetails = useAppSelector(getApplicationDetails) as ApplicationDetailsInterface;

		const { isPending: isLoadingGenerateSignLinkValidation, startRequest: generateSignLinkValidation } =
			useTriggeredRequest(AccountClientRequests.Applications.generateSignLinkValidation);

		const handleFetchGenerateSignLink = useCallback(async () => {
			if (applicationDetails?.current_custodian_request?.id) {
				await generateSignLinkValidation({ custodian_request_id: applicationDetails.current_custodian_request.id });
			}
		}, [applicationDetails?.current_custodian_request?.id, generateSignLinkValidation]);

		useEffect(() => {
			handleFetchGenerateSignLink();
		}, [handleFetchGenerateSignLink]);

		return isLoadingGenerateSignLinkValidation || props.showLoader ? (
			<Loader loaderFallBackComponent={AccountDetailsSkeleton} />
		) : (
			<WrappedComponent {...props} />
		);
	};
}

export default withApplicationValidations;
